'use client';
import { EmojiObjects, Info, Psychology } from '@mui/icons-material';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { mergeAttributes, Node } from '@tiptap/core';
import {
  NodeViewContent,
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';
import { type ReactNode, useEffect, useState } from 'react';

type HintType = 'mnemonic' | 'reminder' | 'info';

const iconMap: Record<HintType, ReactNode> = {
  mnemonic: <EmojiObjects fontSize="small" />,
  reminder: <Psychology fontSize="small" />,
  info: <Info fontSize="small" />,
};

const typeLabel: Record<HintType, string> = {
  mnemonic: 'Eselsbrücke',
  reminder: 'Merkhilfe',
  info: 'Info',
};

function HintBlockComponent(props: NodeViewProps) {
  const isEditable = props.editor.isEditable;
  const { node, updateAttributes } = props;
  const { type = 'info' } = node.attrs as { type: HintType };

  const [variant, setVariant] = useState<HintType>(type);

  useEffect(() => {
    updateAttributes({ type: variant });
  }, [updateAttributes, variant]);

  const customColor = {
    mnemonic: '#623CEA',
    reminder: '#087E8B',
    info: '#08578B',
  }[variant];

  return (
    <NodeViewWrapper className="hint-block" data-type="hint-block">
      {/* This can't be a MUI component as NodeViewContent focus will be blocked */}
      <div
        style={{
          borderLeft: `4px solid ${customColor}`,
          borderRadius: '4px',
          backgroundColor: `${customColor}10`,
          padding: '8px 16px',
          margin: '16px 0',
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 0.25,
          }}>
          {iconMap[variant]}
          <Typography
            variant="body1"
            sx={{ m: '0 0 0 8px !important', fontWeight: 'bold !important' }}>
            {typeLabel[variant]}
          </Typography>
          {isEditable && (
            <Box sx={{ marginLeft: 'auto' }}>
              {(['info', 'mnemonic', 'reminder'] as HintType[]).map((type) => (
                <Tooltip key={type} title={typeLabel[type]}>
                  <IconButton
                    size="small"
                    onClick={() => setVariant(type)}
                    color={variant === type ? 'primary' : 'default'}>
                    {iconMap[type]}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          )}
        </Box>
        <NodeViewContent
          as="div"
          style={
            isEditable
              ? {
                  marginTop: '8px',
                  minHeight: '1rem',
                  border: '1px dashed #ffffff20',
                  borderRadius: '4px',
                  padding: '0 4px',
                }
              : undefined
          }
        />
      </div>
    </NodeViewWrapper>
  );
}

export const HintBlock = Node.create({
  name: 'hint_block',
  group: 'block',
  content: 'inline*',
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      type: { default: 'info' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="hint-block"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(HintBlockComponent);
  },
});
